import { ErrorMessage } from 'views/components';
import isEmpty from 'lodash/isEmpty';
import { optionActions } from 'core/Option';
import { useTranslation } from 'react-i18next';
import { validationSchema } from 'shared/utils';
import {
    ButtonFooter,
    Card,
    Checkbox,
    Container,
    ExpandButton,
    FormGroup,
    Input,
    Loader
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import {
    FrequencyPicker,
    RiskAgentTypePicker
} from 'views/pickers';
import React, { useEffect } from 'react';
import {
    useArray,
    useDispatch,
    useErrors,
    useObjectState,
    useSelector,
    useWindowSize
} from 'hooks';
import {
    useHistory,
    useParams
} from 'react-router-dom';

const DEFAULT_RISK_AGENT = {
    cas: '',
    code: '',
    name: '',
    observations: '',
    risk_agent_type_id: null
};

const RiskAgentRegistration = () => {

    const dispatch = useDispatch();
    const { push } = useHistory();
    const { t } = useTranslation();
    const { id: stringId } = useParams();
    const id = stringId === undefined ? 0 : Number(stringId);
    const createMode = id === 0;
    
    const {
        controlTypesDetail,
        controlTypesDetailLoading,
        riskAgentsError,
        riskAgentsLoading,
        riskAgentsSelected
    } = useSelector('option');

    const [form, setForm, setValue] = useObjectState({ ...DEFAULT_RISK_AGENT });
    const [currentControls, setCurrentControls, {
        find: currentControlsFind,
        push: currentControlsPush,
        remove: currentControlsRemove,
        replace: currentControlReplace
    }] = useArray([], { type: 'object' });
    const { clearError, clearErrors, errors, setErrors } = useErrors();
    const { xs } = useWindowSize();

    useEffect(() => {
        dispatch(optionActions.setOptionRiskAgentsError({}));
        dispatch(optionActions.getControlTypesDetail());
        dispatch(optionActions.setOptionRiskAgentsSelected({}));
    }, []);

    useEffect(() => {
        if (!id) return;
        dispatch(optionActions.getRiskAgent(id));
    }, [id]);

    useEffect(() => {
        if (isEmpty(riskAgentsSelected)) {
            clearErrors();
            setForm({ ...DEFAULT_RISK_AGENT });
            setCurrentControls([]);
            return;
        }
        const { controls, id, type, ...form } = riskAgentsSelected;
        setForm({
            ...form,
            risk_agent_type_id: type?.id
        });
        setCurrentControls(controls.map(control => ({
            control_id: control?.control?.id,
            frequency_id: control?.frequency?.id ?? 0,
            id: control?.id
        })));
    }, [riskAgentsSelected]);

    const handleChange = (id, value) => {
        clearError(id);
        if (riskAgentsError[id]) {
            dispatch(optionActions.setOptionRiskAgentsError(clearObject({
                ...riskAgentsError,
                [id]: undefined
            })));
        }
        setValue(id, value);
    };

    const handleControlClick = (controlId, nextValue) => {
        if (nextValue) {
            currentControlsPush({ control_id: controlId });
            return;
        }
        currentControlsRemove('control_id', controlId);
    };

    const handleFrequencyChange = (controlId, value) => {
        const currentControl = currentControlsFind('control_id', controlId);
        if (!currentControl) return;
        currentControlReplace({
            ...currentControl,
            frequency_id: value
        }, currentControl, 'control_id');
    };

    const onSubmit = () => {
        if (!validate()) return;
        const parsedControls = currentControls.map(({
            control_id,
            id,
            frequency_id
        }) => ({
            control_id,
            ...(!!frequency_id && { frequency_id }),
            ...(!!id && { id })
        }));
        const data = {
            ...form,
            controls: parsedControls,
            ...(!!id && { id })
        };
        dispatch(optionActions.upsertRiskAgent(data));
    };

    const validate = () => {
        const { errors, isValid } = validationSchema({
            name: [!!form.name, t('Requerido')],
            risk_agent_type_id: [!!form.risk_agent_type_id, t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    return (
        <Container>
            <Card>
                <Row>
                    <Col md="4" xs="12">
                        <FormGroup label={ t('Tipo') }>
                            <RiskAgentTypePicker
                                hasError={ !!errors?.risk_agent_type_id }
                                value={ form?.risk_agent_type_id }
                                onChange={ value => handleChange('risk_agent_type_id', value) }
                            />
                            <ErrorMessage relative error={ errors?.risk_agent_type_id } margin="0px" />
                        </FormGroup>
                    </Col>
                    <Col md="4" xs="12">
                        <FormGroup label={ t('Nombre') }>
                            <Input
                                error={ errors?.name ?? riskAgentsError?.name }
                                value={ form?.name }
                                onChange={ value => handleChange('name', value) }
                            />
                        </FormGroup>
                    </Col>
                    <Col md="4" xs="12">
                        <FormGroup label={ t('Código') }>
                            <Input
                                error={ errors?.code }
                                value={ form?.code }
                                onChange={ value => handleChange('code', value) }
                            />
                        </FormGroup>
                    </Col>
                    <Col md="4" xs="12">
                        <FormGroup label={ t('CAS') }>
                            <Input
                                error={ errors?.cas }
                                value={ form?.cas }
                                onChange={ value => handleChange('cas', value) }
                            />
                        </FormGroup>
                    </Col>
                    <Col xs="12">
                        <FormGroup label={ t('Observaciones') }>
                            <Input
                                error={ errors?.observations }
                                height="100px"
                                type="area"
                                value={ form?.observations }
                                onChange={ value => handleChange('observations', value) }
                            />
                        </FormGroup>
                    </Col>
                    <Loader active={ controlTypesDetailLoading } />
                    {
                        !controlTypesDetailLoading && !isEmpty(controlTypesDetail) &&
                        <FormGroup label={ t('Controles y frecuencia') }>
                            <Card margin="10px 0px 0px 0px" width="100%">
                                {
                                    controlTypesDetail.map(controlType => {
                                        const { controls, id, name } = controlType;
                                        if (isEmpty(controls)) return null;
                                        return (
                                            <FormGroup key={ `control-type-${ id }` } label={ name } margin="10px 0px 10px 0px" width="100%">
                                                {
                                                    controls.map(control => {
                                                        const { id, name } = control;
                                                        const currentControl = currentControlsFind('control_id', id);
                                                        const isChecked = !!currentControl;
                                                        return (
                                                            <Container flexDirection={ xs ? 'column' : 'row' } key={ `control-${ id }` } width="100%">
                                                                <Checkbox
                                                                    checked={ isChecked }
                                                                    label={ name }
                                                                    margin="5px 0px"
                                                                    onClick={ value => handleControlClick(id, value) }
                                                                />
                                                                {
                                                                    isChecked &&
                                                                    <FrequencyPicker
                                                                        height="20px"
                                                                        margin="2px 0px 2px 5px"
                                                                        value={ currentControl?.frequency_id }
                                                                        width="200px"
                                                                        onChange={ value => handleFrequencyChange(id, value) }
                                                                    />
                                                                }
                                                            </Container>
                                                        );
                                                    })
                                                }
                                            </FormGroup>
                                        );
                                    })
                                }
                            </Card>
                        </FormGroup>
                    }
                </Row>
            </Card>
            <ButtonFooter margin="0px 0px 20px 0px">
                <ExpandButton
                    danger
                    caption={ createMode ? t('Volver') : t('Cancelar') }
                    disabled={ riskAgentsLoading }
                    size={ 100 }
                    onClick={ () => push(t('/agentes')) }
                />
                <ExpandButton
                    caption={ t('Aceptar') }
                    loading={ riskAgentsLoading }
                    size={ 100 }
                    onClick={ onSubmit }
                />
            </ButtonFooter>
        </Container>
    );
};

export default RiskAgentRegistration;
